import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQLoader from '../../components/loader';
import UQCode from '../../components/code';
import UQSpacer from '../../components/spacer';
import UQGrid from '../../components/grid';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const LoaderPage = () => {
  const colors: Array<'blue' | 'turquois' | 'mint' | 'green' | 'yellow' | 'peach' | 'salmon' | 'purple'> = [
    'blue',
    'turquois',
    'mint',
    'green',
    'yellow',
    'peach',
    'salmon',
    'purple',
  ];

  const sizes: Array<'s' | 'm' | 'l' | 'xl'> = ['s', 'm', 'l', 'xl'];
  const animations: Array<'spin' | 'clip' | 'spin-rainbow' | 'clip-rainbow' | 'cube' | 'cube-rainbow' | 'cube-rainbow-static'> = [
    'spin',
    'clip',
    'spin-rainbow',
    'clip-rainbow',
    'cube',
    'cube-rainbow',
    'cube-rainbow-static',
  ];

  const attributes = [
    {
      attribute: 'size',
      type: "'s' | 'm' | 'l' | 'xl'",
      default: "'m'",
      description: 'The size of the loader.',
    },
    {
      attribute: 'color',
      type: "'blue' | 'turquois' | 'mint' | 'green' | 'yellow' | 'peach' | 'salmon' | 'purple'",
      default: "'blue'",
      description: 'The color of the loader.',
    },
    {
      attribute: 'animation',
      type: "'spin' | 'clip' | 'spin-rainbow' | 'clip-rainbow' | 'cube' | 'cube-rainbow' | 'cube-rainbow-static'",
      default: "'spin'",
      description:
        "The animation style of the loader. 'spin-rainbow', 'clip-rainbow', and 'cube-rainbow' smoothly cycle through all colorwheel colors. 'cube' uses authentic brand colors, 'cube-rainbow' spins with colors, 'cube-rainbow-static' changes colors without spinning.",
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Loader']} />
      <h1>UQLoader Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQLoader</code> component is a versatile loader that supports various sizes, colors, and animations. It is designed to be lightweight and customizable for different use cases.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Default Loader */}
      <h2>Default Loader</h2>
      <UQSpacer size="s" />
      <div style={{ textAlign: 'center' }}>
        <UQLoader />
      </div>
      <UQSpacer size="s" />
      <UQCode code={`<UQLoader />`} enableCopy />
      <UQSpacer size="m" />

      {/* Color Variations */}
      <h2>Color Variations</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        {colors.map((color) => (
          <div key={color} style={{ textAlign: 'center' }}>
            <UQLoader color={color} />
            <p>{color}</p>
          </div>
        ))}
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQGrid gap="1rem" justify="start">\n` +
          colors
            .map((color) => `  <UQLoader color="${color}" />`)
            .join('\n') +
          `\n</UQGrid>`}
        enableCopy
      />
      <UQSpacer size="m" />

      {/* Size Variations */}
      <h2>Size Variations</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        {sizes.map((size) => (
          <div key={size} style={{ textAlign: 'center' }}>
            <UQLoader size={size} />
            <p>{size}</p>
          </div>
        ))}
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQGrid gap="1rem" justify="start">\n` +
          sizes
            .map((size) => `  <UQLoader size="${size}" />`)
            .join('\n') +
          `\n</UQGrid>`}
        enableCopy
      />
      <UQSpacer size="m" />

      {/* Animation Variations */}
      <h2>Animation Variations</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        {animations.map((animation) => (
          <div key={animation} style={{ textAlign: 'center' }}>
            <UQLoader animation={animation} />
            <p>{animation}</p>
          </div>
        ))}
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQGrid gap="1rem" justify="start">\n` +
          animations
            .map((animation) => `  <UQLoader animation="${animation}" />`)
            .join('\n') +
          `\n</UQGrid>`}
        enableCopy
      />
      <UQSpacer size="m" />

      {/* Combined Variations */}
      <h2>Combined Variations</h2>
      <UQSpacer size="s" />
      <UQGrid gap="1rem" justify="start">
        {colors.map((color) =>
          sizes.map((size) => (
            <div key={`${color}-${size}`} style={{ textAlign: 'center' }}>
              <UQLoader color={color} size={size} />
              <p>
                {color} - {size}
              </p>
            </div>
          ))
        )}
      </UQGrid>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQGrid gap="1rem" justify="start">\n` +
          colors
            .map((color) =>
              sizes
                .map(
                  (size) =>
                    `  <UQLoader color="${color}" size="${size}" />`
                )
                .join('\n')
            )
            .join('\n') +
          `\n</UQGrid>`}
        enableCopy
      />
    </>
  );
};

export default LoaderPage;